import { logEvent, setUserId, setUserProperties } from 'firebase/analytics';
import { analyticsReady } from './firebase';

// The one place the client talks to GA4.
//
// Until now Firebase Analytics was initialized and blind: `getAnalytics` ran in
// firebase.js and not a single `logEvent` call existed anywhere, so GA4 saw
// page views and sessions and nothing about the product. These helpers are what
// the rest of the app calls instead of reaching for `logEvent` directly, for
// three reasons that each cost somebody something to learn.
//
// **Analytics can legitimately be absent.** firebase.js resolves it behind
// `isSupported()`, which is false in Firefox with strict tracking protection,
// behind most ad blockers, and in the Jest environment. Every call here waits on
// that promise and quietly does nothing when the answer is no — so callers never
// need a null check, and never need to care that the instance arrives
// asynchronously a moment after the first render. The cost of that tolerance is
// that GA4 undercounts by however many users block it: anything that has to be
// exact belongs in the nightly Firestore aggregation, not here.
//
// **Nothing here may throw.** A failed measurement call must never take down the
// action being measured — an upload that worked should not report as failed
// because a tracker was blocked mid-flight.
//
// **No personal data, ever.** See `scrub` below.

// Google's terms prohibit sending personally identifying data to GA4, and the
// penalty is losing the property, not a warning. The uid passed to `setUserId`
// is pseudonymous and explicitly allowed; an email address or a display name is
// not — and the tempting parameter is always right there, since the code logging
// `invite_mailto_opened` is holding the invitee's address when it does.
//
// This drops such a parameter rather than trusting every future call site to
// remember, and says so loudly in development where someone can still fix it.
const IDENTIFYING = /e-?mail|display_?name|full_?name|phone|address/i;

function scrub(params) {
  if (!params) return undefined;
  const out = {};
  for (const [key, value] of Object.entries(params)) {
    if (IDENTIFYING.test(key)) {
      if (process.env.NODE_ENV === 'development') {
        console.warn(`[analytics] dropped identifying parameter "${key}" — GA4 must not receive personal data`);
      }
      continue;
    }
    if (value === undefined || value === null) continue;
    out[key] = value;
  }
  return out;
}

// Everything below funnels through here, so "wait for the instance, skip if
// there isn't one, never throw" is written once.
//
// Promise.resolve wraps analyticsReady rather than using it directly because a
// unit test mocking ../utils/firebase supplies whatever fields it needs and no
// more; an absent export must behave like an unsupported browser, not like a
// crash in the code being tested.
function withAnalytics(fn, onError) {
  Promise.resolve(analyticsReady)
    .then((instance) => { if (instance) fn(instance); })
    .catch((e) => { if (onError) onError(e); });
}

/**
 * Records a product event.
 *
 * Deliberately fire-and-forget: it returns nothing to await, because no caller
 * should ever sequence real work behind a measurement call.
 *
 * Call it where the write *succeeded*, not where the button was clicked. Every
 * write goes through the API tier, so a click is a request to do something and
 * an event named `person_added` should mean a person exists.
 */
export function track(name, params) {
  withAnalytics(
    (instance) => logEvent(instance, name, scrub(params)),
    (e) => console.warn('[analytics] could not record', name, e),
  );
}

/**
 * Ties this browser's sessions to a Firebase uid, so GA4 stitches the same
 * person together across devices, and clears it on sign-out.
 */
export function identify(uid) {
  withAnalytics((instance) => setUserId(instance, uid || null));
}

/**
 * Sets the `tier` user property, which is what lets every GA4 report be split by
 * plan — the free/paid difference is the question asked of nearly all of them.
 */
export function setTier(tier) {
  withAnalytics((instance) => setUserProperties(instance, { tier: tier || 'free' }));
}

/**
 * Buckets a byte count for `size_bucket`.
 *
 * A raw byte count would be a high-cardinality parameter that GA4 can only show
 * as a list of unique values; buckets are what make "are people uploading
 * phone-camera originals or thumbnails" answerable in the UI. The boundaries sit
 * either side of what an iPhone photo weighs after `compressImage` has run.
 */
export function sizeBucket(bytes) {
  const kb = (bytes || 0) / 1024;
  if (kb < 100) return 'under_100kb';
  if (kb < 500) return '100kb_500kb';
  if (kb < 2048) return '500kb_2mb';
  if (kb < 10240) return '2mb_10mb';
  return 'over_10mb';
}
